import { useState, useEffect, createContext, useContext } from 'react';

// Create Auth Context
const AuthContext = createContext();
function getBaseURL() {
     // Use full URL from env if set
     const fromEnv = (process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');

     // If running on localhost:3000 (dev server) AND env points to localhost, use relative path for proxy
     if (
          /^https?:\/\/localhost(\/|(?::\d+\/))/i.test(fromEnv) &&
          window.location.hostname === 'localhost' &&
          window.location.port === '3000'
     ) {
          try {
               const u = new URL(fromEnv);
               return u.pathname || '/';
          } catch {
               return fromEnv.replace(/^https?:\/\/[^/]+/, '');
          }
     }

     // Otherwise, use full URL from env (for production)
     return fromEnv;
}


// Always returns a full URL in production, relative in dev if proxy is used
export function getApiUrl(endpoint) {
     const base = getBaseURL();
     // If base starts with http, it's a full URL; otherwise, it's a relative path
     if (/^https?:\/\//i.test(base)) {
          return `${base.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;
     }
     // Relative path for dev proxy
     return `/${base.replace(/^\/+|\/+$/g, '')}/${endpoint.replace(/^\/+/, '')}`;
}

// Auth Provider Component
export function AuthProvider({ children }) {
     const [user, setUser] = useState(null);
     const [loading, setLoading] = useState(true);
     const [token, setToken] = useState(null);

     useEffect(() => {
          // Check for existing token on component mount
          checkAuthStatus();
     }, []);

     const checkAuthStatus = () => {
          try {
               const storedToken = sessionStorage.getItem('authToken');
               const storedUser = sessionStorage.getItem('userData');

               if (storedToken && storedUser) {
                    setToken(storedToken);
                    setUser(JSON.parse(storedUser));
               }
          } catch (error) {
               console.error('Error checking auth status:', error);
               // Clear invalid data
               sessionStorage.removeItem('authToken');
               sessionStorage.removeItem('userData');
          } finally {
               setLoading(false);
          }
     };

     const login = async (userCode, password) => {
          try {
               setLoading(true);

               // IMPORTANT: use relative path in dev to avoid CORS, absolute in prod
               const BASE_URL = getBaseURL(); // e.g. "/radiology_badges/api" in dev
               const url = `${BASE_URL}/login`;
               console.debug('[Auth] login →', { url, BASE_URL, userCode });

               // Avoid preflight: do NOT set Content-Type manually; use URLSearchParams
               const form = new URLSearchParams();
               form.append('emp_code', userCode);  // match your backend
               form.append('password', password);

               // Make sure no stale token interferes
               sessionStorage.removeItem('authToken');
               sessionStorage.removeItem('token');

               const res = await fetch(url, {
                    method: 'POST',
                    body: form,     // Browser sets application/x-www-form-urlencoded; charset=UTF-8
                    mode: 'cors',
               });

               // const data = await res.json().catch(() => ({}));
               // console.debug('[Auth] login response', { status: res.status, data });

               const text = await res.text();
               console.log('[Auth] login raw response:', text);
               let data = {};
               try {
                    data = JSON.parse(text);
               } catch (e) {
                    console.error('[Auth] Failed to parse JSON:', e, text);
                    // Optionally, show a user-friendly error or handle fallback
               }
               console.debug('[Auth] login response', { status: res.status, data });


               if (!res.ok || data.status !== true || !data.token) {
                    const msg = data?.message || `Login failed (${res.status})`;
                    return { success: false, error: msg };
               }

               // Store token first so /profile can use it
               sessionStorage.setItem('authToken', data.token);
               sessionStorage.setItem('token', data.token);

               // Fetch profile details and use them for session
               const profileUrl = getApiUrl('/profile');
               let profileData = null;
               try {
                    const pRes = await fetch(profileUrl, {
                         method: 'GET',
                         headers: {
                              // Same-origin in dev via proxy → no CORS preflight
                              'Authorization': `Bearer ${data.token}`,
                              'Accept': 'application/json',
                         },
                         mode: 'cors',
                    });
                    const pJson = await pRes.json().catch(() => ({}));
                    if (pRes.ok && pJson?.status === true && pJson?.profile) {
                         profileData = pJson.profile;
                    } else {
                         console.warn('[Auth] profile fetch failed or invalid:', pRes.status, pJson);
                    }
               } catch (e) {
                    console.warn('[Auth] profile request error:', e);
               }

               // Map to user object (prefer profile fields)
               const userData = profileData
                    ? {
                         user_code: profileData.emp_code || userCode,
                         user_name: profileData.comp_name || data.user_name || '',
                         name: profileData.comp_name || data.user_name || '',
                         role: profileData.role || data.role || '',
                         designation: profileData.designation || '',
                         validity: data.validity, // keep if backend provides, else undefined
                         is_admin: (profileData.role === 'SUPER_ADMIN' || profileData.role === 'ADMIN') ? 'Y' : 'N',
                    }
                    : {
                         // Fallback to login payload if profile not available
                         user_code: data.emp_code || data.user_code || userCode,
                         user_name: data.user_name,
                         name: data.user_name,
                         role: data.role,
                         validity: data.validity,
                         is_admin: data.is_admin,
                    };

               // Persist user
               sessionStorage.setItem('userData', JSON.stringify(userData));
               sessionStorage.setItem('user', JSON.stringify(userData));

               setToken(data.token);
               setUser(userData);

               return { success: true, data: userData };
          } catch (err) {
               console.error('[Auth] login error:', err);
               return { success: false, error: 'Network error. Please try again.' };
          } finally {
               setLoading(false);
          }
     };

     const login1 = async (userCode, password) => {
          try {
               setLoading(true);

               // IMPORTANT: use relative path in dev to avoid CORS, absolute in prod
               const BASE_URL = getBaseURL(); // e.g. "/radiology_badges/api" in dev
               const url = `${BASE_URL}/login`;
               console.debug('[Auth] login →', { url, BASE_URL, userCode });

               // Avoid preflight: do NOT set Content-Type manually; use URLSearchParams
               const form = new URLSearchParams();
               form.append('emp_code', userCode);  // match your backend
               form.append('password', password);

               // Make sure no stale token interferes
               sessionStorage.removeItem('authToken');
               sessionStorage.removeItem('token');

               const res = await fetch(url, {
                    method: 'POST',
                    body: form,     // Browser sets application/x-www-form-urlencoded; charset=UTF-8
                    mode: 'cors',
               });

               const data = await res.json().catch(() => ({}));
               console.debug('[Auth] login response', { status: res.status, data });

               if (!res.ok || data.status !== true || !data.token) {
                    const msg = data?.message || `Login failed (${res.status})`;
                    return { success: false, error: msg };
               }

               const userData = {
                    user_code: data.emp_code || data.user_code || userCode,
                    user_name: data.user_name,
                    name: data.user_name,
                    email: `${data.emp_code || data.user_code || userCode}@vdc.com`,
                    role: data.role,
                    validity: data.validity,
                    is_admin: data.is_admin,
               };

               sessionStorage.setItem('authToken', data.token);
               sessionStorage.setItem('userData', JSON.stringify(userData));
               // keep compatibility keys
               sessionStorage.setItem('token', data.token);
               sessionStorage.setItem('user', JSON.stringify(userData));

               setToken(data.token);
               setUser(userData);

               return { success: true, data: userData };
          } catch (err) {
               console.error('[Auth] login error:', err);
               return { success: false, error: 'Network error. Please try again.' };
          } finally {
               setLoading(false);
          }
     };

     const logout = () => {
          // Clear session storage
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('userData');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          // local storage if used
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('token');
          localStorage.removeItem('user');

          // Reset state
          setToken(null);
          setUser(null);
     };

     const isAuthenticated = () => {
          return !!(token && user);
     };

     const hasRole = (requiredRole) => {
          if (!user || !user.role) return false;
          return user.role === requiredRole;
     };

     const hasAnyRole = (requiredRoles) => {
          if (!user || !user.role) return false;
          return requiredRoles.includes(user.role);
     };

     const isAdmin = () => {
          return user?.is_admin === 'Y' || user?.role === 'QUALITY_ADMIN' || user?.role === 'SUPER_ADMIN';
     };

     const isQualityAdmin = () => {
          return user?.role === 'QUALITY_ADMIN' || user?.role === 'SUPER_ADMIN';
     };

     const isSuperAdmin = () => {
          return user?.role === 'SUPER_ADMIN';
     };

     const isTechnician = () => {
          return user?.role === 'TECHNICIAN';
     };

     const value = {
          user,
          token,
          loading,
          login,
          logout,
          isAuthenticated,
          hasRole,
          hasAnyRole,
          isAdmin,
          isSuperAdmin,
          checkAuthStatus,
          isTechnician,
          isQualityAdmin,
     };

     return (
          <AuthContext.Provider value={value}>
               {children}
          </AuthContext.Provider>
     );
}

// Custom hook to use auth context
export function useAuth() {
     const context = useContext(AuthContext);
     if (!context) {
          throw new Error('useAuth must be used within an AuthProvider');
     }
     return context;
}

// Protected Route Component
export function ProtectedRoute({ children, requiredRole, requiredRoles, allowedRoles, adminOnly, superAdminOnly, qualityAdminOnly }) {
     const { isAuthenticated, loading, hasRole, hasAnyRole, isAdmin, isSuperAdmin, isQualityAdmin } = useAuth();

     if (loading) {
          return (
               <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                    <div className="spinner-border text-primary" role="status">
                         <span className="sr-only">Loading...</span>
                    </div>
               </div>
          );
     }

     if (!isAuthenticated()) {
          return null; // Will show login page
     }

     // Check role-based access
     if (superAdminOnly && !isSuperAdmin()) {
          return (
               <div className="container-fluid mt-5">
                    <div className="alert alert-danger text-center">
                         <h4>Access Denied</h4>
                         <p>This section is only accessible to Super Administrators.</p>
                    </div>
               </div>
          );
     }

     if (adminOnly && !isAdmin()) {
          return (
               <div className="container-fluid mt-5">
                    <div className="alert alert-danger text-center">
                         <h4>Access Denied</h4>
                         <p>This section is only accessible to Administrators.</p>
                    </div>
               </div>
          );
     }

     if (qualityAdminOnly && !isQualityAdmin()) {
          return (
               <div className="container-fluid mt-5">
                    <div className="alert alert-danger text-center">
                         <h4>Access Denied</h4>
                         <p>This section is only accessible to Quality Administrators.</p>
                    </div>
               </div>
          );
     }

     if (requiredRole && !hasRole(requiredRole)) {
          return (
               <div className="container-fluid mt-5">
                    <div className="alert alert-danger text-center">
                         <h4>Access Denied</h4>
                         <p>You need the role "{requiredRole}" to access this section.</p>
                    </div>
               </div>
          );
     }

     // Handle allowedRoles (alias for requiredRoles for backward compatibility)
     const rolesToCheck = allowedRoles || requiredRoles;

     if (rolesToCheck && !hasAnyRole(rolesToCheck)) {
          return (
               <div className="container-fluid mt-5">
                    <div className="alert alert-danger text-center">
                         <h4>Access Denied</h4>
                         <p>You need one of the following roles to access this section: {rolesToCheck.join(', ')}</p>
                    </div>
               </div>
          );
     }

     return children;
}

// Higher-order component for protected routes (legacy support)
export function withAuth(Component, options = {}) {
     return function AuthenticatedComponent(props) {
          return (
               <ProtectedRoute {...options}>
                    <Component {...props} />
               </ProtectedRoute>
          );
     };
}